import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Space,
  Typography,
  Upload,
  Divider,
  Alert,
  Tag,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  SyncOutlined,
  BookOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import * as XLSX from 'xlsx';
import { 
  NganhHoc, 
  CreateCourseRequest, 
  UpdateCourseRequest, 
  ImportCourseData,
  SyncCoursesRequest 
} from '../../../../shared/types';
import courseService, { CourseWithNganh } from '../../services/courseService';
import nganhHocService from '../../services/nganhHocService';

const { Title, Text } = Typography;
const { Option } = Select;

interface CourseFormData {
  KhoaHocID: string;
  TenKhoaHoc: string;
  NganhHocID: string;
  NamVaoTruong?: number;
  IsActive?: boolean;
}

const CourseManagementPage: React.FC = () => {
  // State management
  const [courses, setCourses] = useState<CourseWithNganh[]>([]);
  const [nganhHocList, setNganhHocList] = useState<NganhHoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CourseWithNganh | null>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterNganhHoc, setFilterNganhHoc] = useState<string>('');

  const [form] = Form.useForm<CourseFormData>();
  const [syncForm] = Form.useForm<{ NganhHocID: string }>();

  // Load data
  useEffect(() => {
    fetchCourses();
    fetchNganhHoc();
  }, [pagination.current, pagination.pageSize, searchText, filterNganhHoc]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await courseService.getCourses({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        nganhHocId: filterNganhHoc || undefined
      });

      if (response.success) {
        setCourses(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total
        }));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      message.error('Không thể tải danh sách khóa học');
    } finally {
      setLoading(false);
    }
  };

  const fetchNganhHoc = async () => {
    try {
      const response = await nganhHocService.getNganhHoc({ limit: 100 });
      if (response.success) {
        setNganhHocList(response.data);
      }
    } catch (error) {
      console.error('Error fetching nganh hoc:', error);
    }
  };

  // Handle table pagination change
  const handleTableChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle filter
  const handleNganhHocFilter = (value: string) => {
    setFilterNganhHoc(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle add new
  const handleAdd = () => {
    setEditingCourse(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle edit
  const handleEdit = (record: CourseWithNganh) => {
    setEditingCourse(record);
    form.setFieldsValue({
      ...record,
      IsActive: record.IsActive !== false
    });
    setModalVisible(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      const response = await courseService.deleteCourse(id);
      if (response.success) {
        message.success('Xóa khóa học thành công');
        fetchCourses();
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      message.error('Không thể xóa khóa học');
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      let response;
      if (editingCourse) {
        response = await courseService.updateCourse(editingCourse.KhoaHocID, values);
      } else {
        response = await courseService.createCourse(values);
      }

      if (response.success) {
        message.success(editingCourse ? 'Cập nhật khóa học thành công' : 'Tạo khóa học thành công');
        setModalVisible(false);
        fetchCourses();
      }
    } catch (error) {
      console.error('Error saving course:', error);
      message.error('Không thể lưu khóa học');
    } finally {
      setLoading(false);
    }
  };

  // Handle import Excel
  const handleImportExcel = () => {
    setImportModalVisible(true);
    setFileList([]);
  };

  // Handle file upload
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        // Skip header row and map data
        const items: ImportCourseData[] = jsonData
          .slice(1)
          .map(row => ({
            KhoaHocID: row[0]?.toString() || '',
            TenKhoaHoc: row[1]?.toString() || '',
            NganhHocID: row[2]?.toString() || '',
            IsActive: row[3] !== false
          }))
          .filter(item => item.KhoaHocID && item.TenKhoaHoc && item.NganhHocID);

        if (items.length === 0) {
          message.error('File không có dữ liệu hợp lệ');
          return;
        }

        setImportLoading(true);
        const response = await courseService.importCourses(items);

        if (response.success) {
          const { inserted, errors } = response.data;
          if (inserted.length > 0) {
            message.success(`Import thành công ${inserted.length} khóa học`);
          }
          if (errors.length > 0) {
            message.warning(`${errors.length} dòng bị lỗi khi import`);
            console.warn('Import errors:', errors);
          }
          setImportModalVisible(false);
          fetchCourses();
        }
      } catch (error) {
        console.error('Error importing file:', error);
        message.error('Lỗi khi đọc file Excel');
      } finally {
        setImportLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent upload
  };

  // Handle sync courses
  const handleSyncCourses = () => {
    setSyncModalVisible(true);
    syncForm.resetFields();
  };

  const handleSyncSubmit = async () => {
    try {
      const values = await syncForm.validateFields();
      setSyncLoading(true);

      const response = await courseService.syncCourses(values);
      if (response.success) {
        const { added, updated } = response.data;
        message.success(`Đồng bộ thành công: ${added} thêm mới, ${updated} cập nhật`);
        setSyncModalVisible(false);
        fetchCourses();
      }
    } catch (error) {
      console.error('Error syncing courses:', error);
      message.error('Không thể đồng bộ khóa học');
    } finally {
      setSyncLoading(false);
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Mã khóa học', 'Tên khóa học', 'Mã ngành', 'Trạng thái'],
      ['17601', 'CĐ Y sĩ da khoa K1', '176', true],
      ['17602', 'CĐ Y sĩ da khoa K2', '176', true],
      ['17801', 'Cao đẳng Dược K9', '178', true],
      ['17901', 'Cao đẳng Điều dưỡng K15', '179', true]
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Courses');
    XLSX.writeFile(wb, 'template_import_courses.xlsx');
  };

  // Table columns
  const columns: ColumnsType<CourseWithNganh> = [
    {
      title: 'Mã khóa học',
      dataIndex: 'KhoaHocID',
      key: 'KhoaHocID',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Tên khóa học',
      dataIndex: 'TenKhoaHoc',
      key: 'TenKhoaHoc',
      ellipsis: true
    },
    {
      title: 'Ngành học',
      dataIndex: 'TenNganh',
      key: 'TenNganh',
      width: 200,
      render: (text: string, record) => (
        <div>
          <div>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.NganhHocID}
          </Text>
        </div>
      )
    },
    {
      title: 'Năm vào trường',
      dataIndex: 'NamVaoTruong',
      key: 'NamVaoTruong',
      width: 130,
      align: 'center' as const,
      render: (year: number) => year ? <Tag color="blue">{year}</Tag> : '-'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'IsActive',
      key: 'IsActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Ngưng'}
        </Tag>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Sửa">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa khóa học"
            description="Bạn có chắc chắn muốn xóa khóa học này?"
            onConfirm={() => handleDelete(record.KhoaHocID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <BookOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Quản lý Khóa học
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchCourses}
              loading={loading}
            >
              Làm mới
            </Button>
          </Space>
        }
      >
        {/* Filters and Actions */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input.Search
              placeholder="Tìm kiếm theo mã khóa hoặc tên khóa..."
              allowClear
              onSearch={handleSearch}
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Lọc theo ngành học"
              style={{ width: '100%' }}
              allowClear
              onChange={handleNganhHocFilter}
            >
              {nganhHocList.map(nganh => (
                <Option key={nganh.NganhHocID} value={nganh.NganhHocID}>
                  {nganh.TenNganh}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                Template
              </Button>
              <Button
                type="default"
                icon={<UploadOutlined />}
                onClick={handleImportExcel}
              >
                Import Excel
              </Button>
              <Button
                type="default"
                icon={<SyncOutlined />}
                onClick={handleSyncCourses}
              >
                Đồng bộ khóa học
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Thêm khóa học
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={courses}
          rowKey="KhoaHocID"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} khóa học`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingCourse ? 'Cập nhật khóa học' : 'Thêm khóa học mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            KhoaHocID: '',
            TenKhoaHoc: '',
            NganhHocID: '',
            NamVaoTruong: new Date().getFullYear(),
            IsActive: true
          }}
        >
          <Form.Item
            label="Mã khóa học"
            name="KhoaHocID"
            rules={[
              { required: true, message: 'Vui lòng nhập mã khóa học' },
              { max: 20, message: 'Mã khóa học không được quá 20 ký tự' }
            ]}
          >
            <Input
              placeholder="Ví dụ: 17601, 17801..."
              disabled={!!editingCourse}
            />
          </Form.Item>

          <Form.Item
            label="Tên khóa học"
            name="TenKhoaHoc"
            rules={[
              { required: true, message: 'Vui lòng nhập tên khóa học' },
              { max: 255, message: 'Tên khóa học không được quá 255 ký tự' }
            ]}
          >
            <Input placeholder="Ví dụ: CĐ Y sĩ da khoa K1" />
          </Form.Item>

          <Form.Item
            label="Ngành học"
            name="NganhHocID"
            rules={[
              { required: true, message: 'Vui lòng chọn ngành học' }
            ]}
          >
            <Select placeholder="Chọn ngành học">
              {nganhHocList.map(nganh => (
                <Option key={nganh.NganhHocID} value={nganh.NganhHocID}>
                  {nganh.TenNganh} ({nganh.NganhHocID})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Năm vào trường"
            name="NamVaoTruong"
            rules={[
              { required: true, message: 'Vui lòng nhập năm vào trường' }
            ]}
          >
            <Select placeholder="Chọn năm vào trường">
              {Array.from({ length: 10 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="IsActive"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Hoạt động"
              unCheckedChildren="Ngưng"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import khóa học từ Excel"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <Alert
          message="Hướng dẫn import"
          description={
            <div>
              <p>1. Tải file template và điền thông tin theo mẫu</p>
              <p>2. Cột 1: Mã khóa học (bắt buộc)</p>
              <p>3. Cột 2: Tên khóa học (bắt buộc)</p>
              <p>4. Cột 3: Mã ngành học (bắt buộc)</p>
              <p>5. Cột 4: Trạng thái (true/false, mặc định true)</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleDownloadTemplate}
            block
          >
            Tải file template Excel
          </Button>

          <Divider>Hoặc</Divider>

          <Upload
            accept=".xlsx,.xls"
            fileList={fileList}
            beforeUpload={handleFileUpload}
            onChange={({ fileList }) => setFileList(fileList)}
            maxCount={1}
          >
            <Button
              icon={<UploadOutlined />}
              loading={importLoading}
              block
            >
              Chọn file Excel để import
            </Button>
          </Upload>
        </Space>
      </Modal>

      {/* Sync Modal */}
      <Modal
        title="Đồng bộ khóa học từ CSDL phụ"
        open={syncModalVisible}
        onOk={handleSyncSubmit}
        onCancel={() => setSyncModalVisible(false)}
        confirmLoading={syncLoading}
        width={500}
      >
        <Alert
          message="Chức năng đồng bộ"
          description="Hệ thống sẽ lấy dữ liệu khóa học từ CSDL phụ theo ngành học được chọn. Các khóa học mới sẽ được thêm, khóa học đã tồn tại sẽ được cập nhật tên."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={syncForm}
          layout="vertical"
        >
          <Form.Item
            label="Chọn ngành học để đồng bộ"
            name="NganhHocID"
            rules={[
              { required: true, message: 'Vui lòng chọn ngành học' }
            ]}
          >
            <Select placeholder="Chọn ngành học">
              {nganhHocList.map(nganh => (
                <Option key={nganh.NganhHocID} value={nganh.NganhHocID}>
                  {nganh.TenNganh} ({nganh.NganhHocID})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CourseManagementPage; 